import React from 'react';
import PropTypes from 'prop-types';
import {Grid, Button, Fab, IconButton, MuiThemeProvider} from '@material-ui/core';
import {createForm, formShape} from 'rc-form';
import {InputField, RichTextEditorField, CheckboxField, RadioButtonField, FileUpload} from 'components/common/fields';
import {isEmpty} from 'lodash/lang';
import {Add as AddIcon, Delete as DeleteIcon, Save as SaveIcon, InfoOutlined as InfoIcon} from '@material-ui/icons';
import {DeletionConfirm} from '../common';
import {deleteQuestion} from 'api/question';
import {addFlashMessage} from 'actions/flash-message';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import FormErrors from '../common/FormErrors';
import {fileUploadPromise, formatProtectedAWSURL, filterNodeProperties} from '../../utils/common';
import SupplementalFileList from './SupplementalFileList';
import {
    PrimaryButton, PrimaryDarkButton, SecondaryButton,
} from '../common/StyledButtons';
import InfoTooltip from '../common/InfoTooltip';
import Typography from '@material-ui/core/Typography';


const supplementFileFields = ['url', 'mimetype', 'fileSize'];

const formatSupplementFiles = (filesList) => filesList.map(file => filterNodeProperties(file, supplementFileFields));


class QuestionForm extends React.Component {
    static propTypes = {
        form: formShape,
        submitting: PropTypes.bool.isRequired,
        onSubmit: PropTypes.func.isRequired,
        initialValues: PropTypes.object.isRequired,
    };

    static defaultProps = {
        initialValues: {},
    };

    constructor(props) {
        super(props);
        const {initialValues, match: {params: {questionType}}} = this.props;
        this.state = {
            questionType: initialValues.questionType || questionType,
            currentSelectedRadioButtonIndex: initialValues.answers ?
                initialValues.answers.findIndex(answer => answer.correct === true) : null,
            selectedIndexToDelete: null,
            answers: initialValues.answers || [],
            supplementFiles: isEmpty(initialValues.supplements) ? [] : formatSupplementFiles(initialValues.supplements)
        };

        this.removeQuestionAnswerConfirmBoxRef = React.createRef();
        this.addQuestionAnswer = this.addQuestionAnswer.bind(this);
        this.removeQuestionAnswer = this.removeQuestionAnswer.bind(this);
        this.showRemoveQuestionAnswerConfirm = this.showRemoveQuestionAnswerConfirm.bind(this);

        this.deletionQuestionConfirmBoxRef = React.createRef();
        this.deleteQuestion = this.deleteQuestion.bind(this);
        this.showDeletionQuestionConfirm = this.showDeletionQuestionConfirm.bind(this);
        this.selectRadioButtonChoice = this.selectRadioButtonChoice.bind(this);
        this.onRichTextEditorChange = this.onRichTextEditorChange.bind(this);

        this.handleFileDrop = this.handleFileDrop.bind(this);
        this.removeFileSupplement = this.removeFileSupplement.bind(this);
    }

    componentDidMount() {
        const {form} = this.props;
        form.setFieldsValue({supplements: {value: this.state.supplementFiles, errors: []}});
    }

    selectRadioButtonChoice(e, index) {
        const {questionType} = this.state;
        let answers = this.state.answers;
        if (questionType === 'multiple_choice') {
            answers[index].correct = !answers[index].correct;
        } else {
            answers.map(e => e.correct = false);
            answers[index].correct = true;
        }

        this.setState({answers});
    }

    addQuestionAnswer(e) {
        /*
        Add a new question/answer to the end of an array.
         */
        e.preventDefault();
        this.setState({answers: [...this.state.answers, {correct: false, content: ''}]});
    }

    showRemoveQuestionAnswerConfirm(e, selectedIndex) {
        this.setState(
            {
                selectedIndexToDelete: selectedIndex
            },
            this.removeQuestionAnswerConfirmBoxRef.current.openModal
        );

    }

    showDeletionQuestionConfirm() {
        this.deletionQuestionConfirmBoxRef.current.openModal();
    }

    removeQuestionAnswer(selectedIndex) {
        /*
        Remove the question/answer from the array.
         */
        let {currentSelectedRadioButtonIndex} = this.state;
        let answers = [...this.state.answers];
        answers.splice(selectedIndex, 1);
        if (selectedIndex === currentSelectedRadioButtonIndex) {
            currentSelectedRadioButtonIndex = null;
        } else if (currentSelectedRadioButtonIndex !== null && selectedIndex < currentSelectedRadioButtonIndex) {
            currentSelectedRadioButtonIndex--;
        }
        this.setState({answers, currentSelectedRadioButtonIndex});
    }

    onRichTextEditorChange(index, value) {
        let answers = this.state.answers;
        answers[index].content = value;
        this.setState({answers});
    }

    deleteQuestion() {
        /*
        Delete the question.
         */
        const {initialValues, addFlashMessage} = this.props;
        deleteQuestion(initialValues._id.$oid)
            .then(() => {
                this.context.router.history.push('/questions');
                addFlashMessage('Question has been successfully deleted!', 'success');
            })
            .catch(() => {
                addFlashMessage('Failed deleting question!', 'error');
            });
    }

    handleFileDrop(files) {
        const {supplementFiles} = this.state;
        const {form} = this.props;

        fileUploadPromise(files, {isProtected: true}).then(uploadedFiles =>
            uploadedFiles.map(file =>
                ({
                    url: formatProtectedAWSURL(file),
                    file_size: file.size,
                    mimetype: file.type
                }))
        ).then((data) => {
            form.setFieldsValue({supplements: {value: supplementFiles.concat(data), errors: []}});
            this.setState({supplementFiles: supplementFiles.concat(data)});
        });
    }

    removeFileSupplement(selectedIndex) {
        const {form} = this.props;
        const {supplementFiles = []} = this.state;
        supplementFiles.splice(selectedIndex, 1);
        this.setState({supplementFiles}, () => {
            form.setFieldsValue({supplements: {value: supplementFiles, errors: []}});
        });
    }

    render() {
        const {initialValues, form, submitting, onSubmit, isUpdate, errors} = this.props;
        const {selectedIndexToDelete, answers = [], questionType, supplementFiles = []} = this.state;

        return (
            <>
                {isUpdate &&
                <SecondaryButton
                    id="question-delete-icon-button"
                    onClick={this.showDeletionQuestionConfirm}
                    aria-label="Delete Question" size="small"
                >
                    <DeleteIcon size="small"/>
                    Delete Question
                </SecondaryButton>}

                <DeletionConfirm
                    ref={this.deletionQuestionConfirmBoxRef}
                    itemName="question"
                    deleteAction={this.deleteQuestion}
                />

                <DeletionConfirm
                    ref={this.removeQuestionAnswerConfirmBoxRef}
                    itemName="item"
                    deleteAction={() => this.removeQuestionAnswer(selectedIndexToDelete)}
                />

                <Grid container component="form" onSubmit={onSubmit}>
                    <Grid item xs={11}>
                        <InputField
                            name="name"
                            label="Title (not visible to student)"
                            variant="outlined"
                            initialValue={initialValues['name']}
                            rules={[
                                {required: true, message: 'Please enter a title'},
                            ]}
                            form={form}
                            autoComplete="off"
                        /></Grid>
                    <Grid item>
                        <InfoTooltip tooltipText="The question title is used to refer to the question on the quiz
                        creation and update screens. It will not be displayed to the student"/>
                    </Grid>
                    <Grid item xs={12}>
                        <RichTextEditorField
                            name="content"
                            label={<Typography variant={'h6'}>Enter the question below</Typography>}
                            initialValue={initialValues['content']}
                            rules={[
                                {required: true, message: 'Please enter the question'},
                            ]}
                            form={form}
                        />
                        <InputField
                            name="topic"
                            label="Topic"
                            variant="outlined"
                            initialValue={initialValues['topic']}
                            rules={[
                                {required: true, message: 'Please enter a topic name'},
                            ]}
                            form={form}
                            autoComplete="off"
                        />
                        <InputField
                            name="subtopic"
                            label="Subtopic"
                            variant="outlined"
                            initialValue={initialValues['subtopic']}
                            form={form}
                            autoComplete="off"
                        />

                    </Grid>

                    <Grid item xs={12}>
                        <h3>Supplemental Content</h3>
                        <FileUpload
                            name="supplements"
                            label=""
                            initialValue={initialValues['supplements']}
                            form={form}
                            onChange={this.handleFileDrop}
                        />
                        {!isEmpty(supplementFiles) && <SupplementalFileList
                            supplementFiles={supplementFiles}
                            removeFileSupplement={(e, selectedIndex) =>
                                this.removeFileSupplement.bind(e, selectedIndex)}
                        />}
                    </Grid>
                    {!isEmpty(errors) && <FormErrors errors={errors}/>}
                    <Grid item xs={12}>
                        <h3>Question Answers</h3>
                    </Grid>

                    <div className="quiz-question-answer-container">
                        <Grid item xs={12}>
                            <Grid container>
                                <Grid item xs={2} className="quiz-question-answer-grid-item">
                                    Answer
                                </Grid>
                                <Grid item xs={8} className="quiz-question-answer-grid-item">
                                    Description
                                </Grid>
                                <Grid item xs={2} className="quiz-question-answer-grid-item">
                                    Delete
                                </Grid>
                            </Grid>

                            <div className="quiz-question-answer">
                                {!isEmpty(answers) && answers.map((record, index) => {
                                    return (
                                        <Grid key={`quiz-question-answer-${index}`} container
                                            className="quiz-question-answer-grid">
                                            <Grid item xs={2} className="quiz-question-answer-grid-item">
                                                {questionType === 'single_choice' ? (
                                                    <RadioButtonField
                                                        name={`question-answer-correct-answer-${index}`}
                                                        label=""
                                                        onChange={(e) => {
                                                            this.selectRadioButtonChoice(e, index);
                                                        }}
                                                        initialValue={record.correct}
                                                        form={form}
                                                    />
                                                ) : (
                                                    <CheckboxField
                                                        name={`question-answer-correct-answer-${index}`}
                                                        label=""
                                                        onChange={(e) => {
                                                            this.selectRadioButtonChoice(e, index);
                                                        }}
                                                        initialValue={record.correct}
                                                        form={form}
                                                    />
                                                )}
                                            </Grid>
                                            <Grid item xs={8} className="quiz-question-answer-grid-item">
                                                <RichTextEditorField
                                                    name={`question-answer-rtef-${index}`}
                                                    label=""
                                                    initialValue={record.content}
                                                    onChange={e => this.onRichTextEditorChange(index, e)}
                                                    rules={[
                                                        {required: true, message: 'Please enter a question'},
                                                    ]}
                                                    form={form}
                                                />
                                            </Grid>
                                            <Grid item xs={2} className="quiz-question-answer-grid-item">
                                                <IconButton
                                                    type="button"
                                                    onClick={e => this.showRemoveQuestionAnswerConfirm(e, index)}>
                                                    <DeleteIcon color="error"/>
                                                </IconButton>
                                            </Grid>
                                        </Grid>
                                    );
                                })}
                            </div>
                            <Grid container>
                                <Grid item xs={12}>
                                    <PrimaryDarkButton onClick={this.addQuestionAnswer}>
                                        <AddIcon/> &nbsp; Add Option
                                    </PrimaryDarkButton>
                                </Grid>
                            </Grid>
                        </Grid>
                    </div>
                    <Grid container onSubmit={onSubmit}>
                        <Grid item xs={12}>
                            <h3>Question Options</h3>
                            <CheckboxField
                                name="randomize_answers"
                                label="Randomize Answer Order"
                                initialValue={initialValues.randomizeAnswers || false}
                                form={form}
                            />
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <div id="floater" className="quizscape-btn-group text-align-right">
                            <PrimaryButton type="submit" disabled={submitting}>
                                {isUpdate ? 'Update' : <><SaveIcon/>&nbsp; Save</>}
                            </PrimaryButton>
                        </div>
                    </Grid>
                </Grid>
            </>
        );
    }
}

const mapDispatchToProps = (dispatch) => ({
    addFlashMessage: (...args) => dispatch(addFlashMessage(...args)),
});

QuestionForm.contextTypes = {
    router: PropTypes.object.isRequired
};

export default withRouter(connect(
    null,
    mapDispatchToProps
)(createForm()(QuestionForm)));
